import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  mergeClasses,
  Option,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { formatDateToTimeString, TimePicker } from '@fluentui/react-timepicker-compat';
import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { winnerEventRoles, winnerEventTypes } from '../../../models';
import {
  MAX_WINNER_EVENT_ATTENDEES,
  MAX_WINNER_EVENT_NAME_LENGTH,
  MIN_WINNER_EVENT_ATTENDEES,
  WinnerEventRequest,
  winnerEventRoleSchema,
  winnerEventTypeSchema,
} from '../../../models/schemas';
import { ValidationMessageList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput } from '../../shared-components/form/FormInput';
import { SelectOptions } from '../../shared-components/form/SelectOptions';
import './AddOrEditWinnerEventModal.scss';

interface AddOrEditWinnerEventModalProps {
  isModalOpen: boolean;
  onDismissModal(): void;
  onAddWinnerEvent(winnerEvent: WinnerEventRequest): void;
  onUpdateWinnerEvent(winnerEvent: WinnerEventRequest): void;
}

const AddOrEditWinnerEventModalImpl: React.FC<AddOrEditWinnerEventModalProps> = (props) => {
  const {
    isModalOpen,
    onDismissModal,
    onAddWinnerEvent,
    onUpdateWinnerEvent,
  } = props;

  const {
    winnerRequirementPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const formatTimeText = (time?: string): string => {
    return time
      ? formatDateToTimeString(parseISO(`1000-01-01T${time}`), { hourCycle: 'h12' })
      : '';
  };

  const parseTime = (time?: string): Date | null => {
    return time ? parseISO(`1000-01-01T${time}`) : null;
  };

  const handleDismiss = (): void => {
    setIsBeingValidated(false);
    onDismissModal();
  };

  const handleSave = (): void => {
    setIsBeingValidated(true);

    if (
      !winnerRequirementPageUiStore.winnerEventRequestBody
      || !winnerRequirementPageUiStore.winnerEventRequestValidation.isValid
    ) return;

    if (winnerRequirementPageUiStore.addOrEditEventModalType === 'add') {
      onAddWinnerEvent(winnerRequirementPageUiStore.winnerEventRequestBody);
    } else if (winnerRequirementPageUiStore.addOrEditEventModalType === 'update') {
      onUpdateWinnerEvent(winnerRequirementPageUiStore.winnerEventRequestBody);
    }
  };

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open) handleDismiss();
      }}
    >
      <DialogSurface className={mergeClasses(commonStyles.modalSurface, 'add-edit-winner-event-modal')}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle>
            {winnerRequirementPageUiStore.addOrEditEventModalType === 'add' ? 'Add award event' : 'Edit award event'}
          </DialogTitle>
          <DialogContent>
            <FormInput
              required
              label='Name'
              validationMessage={
                isBeingValidated
                && winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.displayName?.length
                  ? (
                    <ValidationMessageList
                      messages={winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.displayName}
                    />
                  )
                  : undefined
              }
              onChange={(value) => winnerRequirementPageUiStore.setWinnerEventRequestBody({
                key: 'displayName',
                value,
              })}
              inputProps={{
                value: winnerRequirementPageUiStore.winnerEventRequestBody?.displayName ?? '',
                maxLength: MAX_WINNER_EVENT_NAME_LENGTH,
              }}
            />
            <SelectOptions
              required
              label='Event type'
              validationMessage={
                isBeingValidated
                && winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.eventType?.length
                  ? (
                    <ValidationMessageList
                      messages={winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.eventType}
                    />
                  )
                  : undefined
              }
              onOptionSelect={(data) => {
                if (!data.optionValue) return;
                winnerRequirementPageUiStore.setWinnerEventRequestBody({
                  key: 'eventType',
                  value: winnerEventTypeSchema.parse(data.optionValue),
                });
              }}
              dropdownProps={{
                value: winnerRequirementPageUiStore.winnerEventRequestBody?.eventType ?? '',
                selectedOptions: winnerRequirementPageUiStore.winnerEventRequestBody?.eventType
                  ? [winnerRequirementPageUiStore.winnerEventRequestBody.eventType]
                  : [],
              }}
            >
              {
                winnerEventTypes.map((eventType) => (
                  <Option
                    key={eventType}
                    value={eventType}
                  >
                    {eventType}
                  </Option>
                ))
              }
            </SelectOptions>
            <FlexBox
              isFullWidth
              className='date-picker-fields'
              content='space-between'
            >
              <Field
                required
                label='Date'
                className='date-picker-field'
                validationMessage={
                  isBeingValidated
                  && winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.eventDate?.length
                    ? (
                      <ValidationMessageList
                        messages={winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.eventDate}
                      />
                    )
                    : undefined
                }
                validationMessageIcon={null}
              >
                <DatePicker
                  id='date-picker'
                  aria-label='Date'
                  allowTextInput
                  value={winnerRequirementPageUiStore.winnerEventRequestBody?.eventDate
                    ? parseISO(winnerRequirementPageUiStore.winnerEventRequestBody.eventDate)
                    : null}
                  onSelectDate={(date) => winnerRequirementPageUiStore.setWinnerEventRequestBody({
                    key: 'eventDate',
                    value: date ? format(date, 'yyyy-MM-dd') : '',
                  })}
                />
              </Field>
              <Field
                required={!winnerRequirementPageUiStore.winnerEventRequestBody?.isTimeTBD}
                label='Start time'
                className='date-picker-field'
                validationMessage={
                  isBeingValidated
                  && winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.startTime?.length
                    ? (
                      <ValidationMessageList
                        messages={winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.startTime}
                      />
                    )
                    : undefined
                }
                validationMessageIcon={null}
              >
                <TimePicker
                  id='start-time-picker'
                  aria-label='Start time'
                  freeform
                  selectedTime={parseTime(winnerRequirementPageUiStore.winnerEventRequestBody?.startTime)}
                  value={formatTimeText(winnerRequirementPageUiStore.winnerEventRequestBody?.startTime)}
                  onTimeChange={(_, data) => winnerRequirementPageUiStore.setWinnerEventRequestBody({
                    key: 'startTime',
                    value: data.selectedTime ? format(data.selectedTime, 'HH:mm:ss') : undefined,
                  })}
                  increment={30}
                  disabled={winnerRequirementPageUiStore.winnerEventRequestBody?.isTimeTBD}
                />
              </Field>
              <Field
                required={!winnerRequirementPageUiStore.winnerEventRequestBody?.isTimeTBD}
                label='End time'
                className='date-picker-field'
                validationMessage={
                  isBeingValidated
                  && winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.endTime?.length
                    ? (
                      <ValidationMessageList
                        messages={winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.endTime}
                      />
                    )
                    : undefined
                }
                validationMessageIcon={null}
              >
                <TimePicker
                  id='end-time-picker'
                  aria-label='End time'
                  freeform
                  selectedTime={parseTime(winnerRequirementPageUiStore.winnerEventRequestBody?.endTime)}
                  value={formatTimeText(winnerRequirementPageUiStore.winnerEventRequestBody?.endTime)}
                  onTimeChange={(_, data) => winnerRequirementPageUiStore.setWinnerEventRequestBody({
                    key: 'endTime',
                    value: data.selectedTime ? format(data.selectedTime, 'HH:mm:ss') : undefined,
                  })}
                  increment={30}
                  disabled={winnerRequirementPageUiStore.winnerEventRequestBody?.isTimeTBD}
                />
              </Field>
            </FlexBox>
            <Field>
              <Checkbox
                label='Time TBD'
                checked={winnerRequirementPageUiStore.winnerEventRequestBody?.isTimeTBD ?? false}
                onChange={(_, data) => winnerRequirementPageUiStore.setWinnerEventRequestBody({
                  key: 'isTimeTBD',
                  value: (data.checked === true),
                })}
              />
            </Field>
            <SelectOptions
              required
              label='Role'
              validationMessage={
                isBeingValidated
                && winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.role?.length
                  ? (
                    <ValidationMessageList
                      messages={winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.role}
                    />
                  )
                  : undefined
              }
              onOptionSelect={(data) => {
                if (!data.optionValue) return;
                winnerRequirementPageUiStore.setWinnerEventRequestBody({
                  key: 'role',
                  value: winnerEventRoleSchema.parse(data.optionValue),
                });
              }}
              dropdownProps={{
                value: winnerRequirementPageUiStore.winnerEventRequestBody?.role ?? '',
                selectedOptions: winnerRequirementPageUiStore.winnerEventRequestBody?.role
                  ? [winnerRequirementPageUiStore.winnerEventRequestBody.role]
                  : [],
              }}
            >
              {
                winnerEventRoles.map((role) => (
                  <Option
                    key={role}
                    value={role}
                  >
                    {role}
                  </Option>
                ))
              }
            </SelectOptions>
            <FormInput
              required
              label='Number of attendees'
              validationMessage={
                isBeingValidated
                && winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.numberOfAttendees?.length
                  ? (
                    <ValidationMessageList
                      messages={winnerRequirementPageUiStore.winnerEventRequestValidation.fieldErrors.numberOfAttendees}
                    />
                  )
                  : undefined
              }
              className='field--attendees'
              onChange={(value) => winnerRequirementPageUiStore.setWinnerEventRequestBody({
                key: 'numberOfAttendees',
                value: Number(value),
              })}
              inputProps={{
                type: 'number',
                value: `${winnerRequirementPageUiStore.winnerEventRequestBody?.numberOfAttendees ?? 0}`,
                min: MIN_WINNER_EVENT_ATTENDEES,
                max: MAX_WINNER_EVENT_ATTENDEES,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={handleDismiss}
            >
              Back
            </Button>
            <Button
              appearance='primary'
              disabled={isBeingValidated && !winnerRequirementPageUiStore.winnerEventRequestValidation.isValid}
              onClick={handleSave}
            >
              {winnerRequirementPageUiStore.addOrEditEventModalType === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const AddOrEditWinnerEventModal = observer(AddOrEditWinnerEventModalImpl);
